import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  Link,
  TextField,
  Typography,
} from '@mui/material';
import Grid2 from '@mui/material/Grid2';
import { Link as RouterLink, useNavigate, useSearchParams } from 'react-router-dom';
import BasePage from '../authentication-layout';
import ProjectLogo from '../../../components/commons/ProjectLogo';
import { projectConfig } from '../../../config/projectConfig';
import { resolveFirebaseWebConfig } from '../../../config/firebaseClient';

type ResetPasswordResponse = {
  email?: string;
  requestType?: string;
};

const mapResetErrorMessage = (message?: string) => {
  switch (message) {
    case 'EXPIRED_OOB_CODE':
      return 'This password setup link has expired. Please ask your administrator to send a new invitation.';
    case 'INVALID_OOB_CODE':
      return 'This password setup link is invalid. Please ask your administrator to send a new invitation.';
    case 'USER_DISABLED':
      return 'This account is currently unavailable. Please speak with your administrator.';
    case 'WEAK_PASSWORD : Password should be at least 6 characters':
    case 'WEAK_PASSWORD':
      return 'Your password must be at least 6 characters long.';
    default:
      return 'We could not complete your password setup. Please ask your administrator to send a new invitation.';
  }
};

const SetPasswordPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [email, setEmail] = useState(searchParams.get('email') || '');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [isVerifying, setIsVerifying] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | undefined>();

  const apiKey = useMemo(
    () =>
      searchParams.get('apiKey') ||
      process.env.REACT_APP_FIREBASE_API_KEY ||
      resolveFirebaseWebConfig().apiKey ||
      '',
    [searchParams]
  );
  const mode = searchParams.get('mode');
  const oobCode = searchParams.get('oobCode');

  useEffect(() => {
    const verifyResetCode = async () => {
      if (mode !== 'resetPassword' || !oobCode || !apiKey) {
        setError('This password setup link is invalid. Please ask your administrator to send a new invitation.');
        setIsVerifying(false);
        return;
      }

      try {
        const response = await fetch(
          `https://identitytoolkit.googleapis.com/v1/accounts:resetPassword?key=${apiKey}`,
          {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
            },
            body: JSON.stringify({ oobCode }),
          }
        );

        const data = (await response.json()) as
          | ResetPasswordResponse
          | { error?: { message?: string } };

        if (!response.ok) {
          setError(mapResetErrorMessage(data && 'error' in data ? data.error?.message : undefined));
          setIsVerifying(false);
          return;
        }

        const resetData = data as ResetPasswordResponse;
        if (resetData.email) {
          setEmail(resetData.email);
        }
      } catch {
        setError('We could not verify this password setup link. Please try again or ask your administrator for a new invitation.');
      } finally {
        setIsVerifying(false);
      }
    };

    void verifyResetCode();
  }, [apiKey, mode, oobCode]);

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    if (!oobCode || !apiKey) {
      setError('This password setup link is invalid. Please ask your administrator to send a new invitation.');
      return;
    }

    if (password.length < 8) {
      setError('Your password must be at least 8 characters long.');
      return;
    }

    if (password !== confirmPassword) {
      setError('Your passwords do not match.');
      return;
    }

    setIsSubmitting(true);
    setError(undefined);

    try {
      const response = await fetch(
        `https://identitytoolkit.googleapis.com/v1/accounts:resetPassword?key=${apiKey}`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            oobCode,
            newPassword: password,
          }),
        }
      );

      const data = (await response.json()) as { error?: { message?: string } };

      if (!response.ok) {
        setError(mapResetErrorMessage(data.error?.message));
        setIsSubmitting(false);
        return;
      }

      navigate(
        `/auth/login?setup=success${email ? `&email=${encodeURIComponent(email)}` : ''}`,
        { replace: true }
      );
    } catch {
      setError('We could not set your password right now. Please try again.');
      setIsSubmitting(false);
    }
  };

  return (
    <BasePage image={projectConfig.bgLogin}>
      <Grid2 container spacing={1} justifyContent="center" alignItems="center" height="100%">
        <Grid2 size={{ xs: 11, sm: 9, md: 5, lg: 4, xl: 3 }}>
          <Card
            sx={theme => ({
              position: 'relative',
              borderRadius: theme.spacing(2),
              padding: theme.spacing(3, 0),
              boxShadow: `0px -2px 20px 2px ${theme.HEPalette.coloredShadows.dark}66`,
            })}
          >
            <Box width="100%" borderRadius="lg" textAlign="center">
              <Typography
                variant="h5"
                sx={theme => ({
                  color: theme.palette.primary.main,
                  mt: 1,
                  fontWeight: 'bold',
                })}
              >
                SET UP PASSWORD
              </Typography>
              <Grid2 container justifyContent="center" sx={{ mt: 2, mb: 2 }}>
                <ProjectLogo size="card" />
              </Grid2>
            </Box>
            <Box px={3}>
              {error && (
                <Alert severity="error" sx={{ mb: 2 }}>
                  {error}
                </Alert>
              )}
              {isVerifying ? (
                <Typography color="text.secondary">
                  Verifying your password setup link...
                </Typography>
              ) : (
                <Box component="form" onSubmit={handleSubmit} noValidate>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    {email
                      ? `Create a password for ${email}. After that, you will be redirected to sign in.`
                      : 'Create your password to finish setting up your account.'}
                  </Typography>
                  <Box mb={1}>
                    <TextField
                      margin="normal"
                      fullWidth
                      label="New Password"
                      type="password"
                      value={password}
                      onChange={event => setPassword(event.target.value)}
                      required
                    />
                  </Box>
                  <Box mb={1}>
                    <TextField
                      margin="normal"
                      fullWidth
                      label="Confirm Password"
                      type="password"
                      value={confirmPassword}
                      onChange={event => setConfirmPassword(event.target.value)}
                      required
                    />
                  </Box>
                  <Box mt={4} mb={1}>
                    <Button
                      type="submit"
                      disabled={isSubmitting || isVerifying}
                      fullWidth
                      variant="contained"
                      sx={theme => ({
                        padding: theme.spacing(1, 0),
                        backgroundColor: theme.palette.primary.main,
                        color: theme.palette.primary.contrastText,
                        '&:hover': {
                          backgroundColor: theme.palette.primary.dark,
                        },
                      })}
                    >
                      {isSubmitting ? 'Saving password...' : 'Set Password'}
                    </Button>
                  </Box>
                  <Box textAlign="center" mt={2}>
                    <Typography variant="body2" color="text.secondary">
                      Already have a password?
                      {' '}
                      <Link component={RouterLink} to="/auth/login" underline="hover">
                        Sign in
                      </Link>
                    </Typography>
                  </Box>
                </Box>
              )}
            </Box>
          </Card>
        </Grid2>
      </Grid2>
    </BasePage>
  );
};

export default SetPasswordPage;
